import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client.safe';

export interface QuickStats {
  cases: number;
  documents: number;
  analyses: number;
  reports: number;
}

export function useQuickStats() {
  const [stats, setStats] = useState<QuickStats>({
    cases: 0,
    documents: 0,
    analyses: 0,
    reports: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    const fetchStats = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        
        if (!user) {
          setLoading(false);
          return;
        }

        // First get user's audit IDs
        const { data: userAudits } = await supabase
          .from('audits')
          .select('id')
          .eq('user_id', user.id);

        const auditIds = userAudits?.map(audit => audit.id) || [];

        // Fetch all stats in parallel for better performance
        const [casesResult, documentsResult, analysesResult, reportsResult] = await Promise.all([
          // Count audits (cases) for current user
          supabase
            .from('audits')
            .select('id', { count: 'exact', head: true })
            .eq('user_id', user.id),
          
          // Count documents across all user's audits (exclude soft-deleted)
          auditIds.length > 0 
            ? supabase
                .from('audit_documents')
                .select('id', { count: 'exact', head: true })
                .in('audit_id', auditIds)
                .is('deleted_at', null)
            : { count: 0 },
          
          // Count analyses/jobs (both completed and pending)
          supabase
            .from('jobs')
            .select('id', { count: 'exact', head: true })
            .eq('user_id', user.id),
          
          // Count final reports for user's audits
          auditIds.length > 0
            ? supabase
                .from('final_reports')
                .select('id', { count: 'exact', head: true })
                .in('audit_id', auditIds)
            : { count: 0 }
        ]);

        if (isMounted) {
          setStats({
            cases: casesResult.count || 0,
            documents: documentsResult.count || 0,
            analyses: analysesResult.count || 0,
            reports: reportsResult.count || 0,
          });
          setError(null);
        }
      } catch (err) {
        if (isMounted) {
          console.error('Error fetching quick stats:', err);
          setError('Kunde inte hämta statistik');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchStats();

    // Set up real-time subscriptions for live updates
    const auditsSub = supabase
      .channel('quick_stats_audits')
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'audits' },
        () => fetchStats()
      )
      .subscribe();

    const documentsSub = supabase
      .channel('quick_stats_documents')
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'audit_documents' },
        () => fetchStats()
      )
      .subscribe();

    const jobsSub = supabase
      .channel('quick_stats_jobs')
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'jobs' },
        () => fetchStats()
      )
      .subscribe();

    const reportsSub = supabase
      .channel('quick_stats_reports')
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'final_reports' },
        () => fetchStats()
      )
      .subscribe();

    return () => {
      isMounted = false;
      auditsSub.unsubscribe();
      documentsSub.unsubscribe();
      jobsSub.unsubscribe();
      reportsSub.unsubscribe();
    };
  }, []);

  return { stats, loading, error };
}